/**
 * ONE EXCHANGE — a question and whatever came back.
 *
 * ══ WHY THIS IS A COMPONENT AND NOT A BRANCH IN THE PAGE ═══════════════════
 *
 * The page renders the LIVE exchange and every FINISHED one from this session.
 * When that switch lived inline in `index.tsx` there was only ever one exchange
 * on screen, so there was nothing to keep in step. With a session record there
 * are two call sites, and two copies of a nine-arm switch drift: the historical
 * copy grows a missing arm, or keeps an old label, and the operator sees two
 * different renderings of the same outcome without knowing which is current.
 *
 * So there is ONE switch, used twice.
 *
 * ══ WHAT `live` CHANGES, AND WHAT IT MUST NOT ══════════════════════════════
 *
 * A finished exchange is READ-ONLY. It carries no actions, because pinning or
 * saving needs the turn id of the turn being acted on, and the session record
 * deliberately keeps only the question and the terminal state — a `turnId`
 * threaded through history would be one more thing to get wrong, and a pin
 * attributed to the wrong turn is worse than no pin.
 *
 * It also carries no live region: `role="log" aria-live="polite"` belongs to
 * the page's conversation section, and re-announcing a two-minute-old answer
 * when a new one arrives would talk over the operator.
 */
import type { AgentScreenState } from '../turn-state';
import { turnServedOperatorRows, type TurnResultPayload } from '../protocol';

import { AnswerCard } from './AnswerCard';
import {
  DeclinedPanel,
  ErrorPanel,
  LoadingState,
  RefusedPanel,
  SlowTurnHint,
  StoppedPanel,
} from './StateViews';
import { ToolTimeline } from './ToolTimeline';
import { natBanner } from '../answer-view';

export function Exchange({
  question,
  state,
  buildEnvironment,
  egressProfile,
  actions,
  onPick,
  onRetry,
  onReset,
}: {
  question: string | null;
  state: AgentScreenState;
  buildEnvironment: string | undefined;
  egressProfile?: string;
  /** Only the LIVE exchange passes these. A finished one is read-only. */
  actions?: React.ReactNode;
  onPick?: (question: string) => void;
  onRetry?: () => void;
  onReset?: () => void;
}) {
  return (
    <div className="space-y-5">
      {question !== null && (
        <p className="ml-auto max-w-[80%] whitespace-pre-wrap break-words rounded-2xl bg-muted px-4 py-3 text-[15px] leading-7">
          {question}
        </p>
      )}

      {state.kind === 'loading' && <LoadingState />}

      {state.kind === 'running' && (
        <div className="space-y-3">
          {/* aria-hidden while it churns: the log is polite, and a per-phase
              announcement would talk over the operator. */}
          <div aria-hidden>
            <ToolTimeline
              phase={state.phase}
              tool={state.tool}
              toolCallsDone={state.toolCallsDone}
              iteration={state.iteration}
              elapsedMs={state.elapsedMs}
            />
          </div>
          {state.slowHint &&
            (() => {
              const banner = natBanner({
                environment: state.environment,
                egressProfile: egressProfile ?? '',
              });
              return banner === null ? null : <SlowTurnHint message={banner.message} />;
            })()}
          <p className="sr-only" role="status">
            Working. {state.toolCallsDone} tool calls completed.
          </p>
        </div>
      )}

      {/* 🔴 NEVER GATE AN OUTCOME ON AN ACTION. These two arms used to require
          `onReset` / `onRetry`, which the HISTORY call site does not pass — so a
          finished `stopped` or `error` exchange rendered NOTHING: the question
          bubble stood alone over blank space, and the record silently dropped
          two of the five kinds it saves. The panels take the handler as
          optional and hide their button when it is absent, exactly as
          `AnswerCard` already does with `actions`. */}
      {state.kind === 'stopped' && <StoppedPanel onReset={onReset} />}

      {state.kind === 'refused' && (
        <RefusedPanel reasonCode={state.reasonCode} message={state.result.prose} />
      )}

      {/* 🔴 A DECLINE NEVER HIDES A TABLE THE TURN ALREADY SERVED. The model's
          last tool call decides what the model SAYS, not whether the operator
          sees rows the disclosure engine delivered to this screen. */}
      {state.kind === 'declined' && !turnServedOperatorRows(state.result) && (
        <DeclinedPanel decline={state.decline} onPick={onPick ?? (() => undefined)} />
      )}

      {state.kind === 'declined' && turnServedOperatorRows(state.result) && (
        <AnswerCard
          result={state.result}
          buildEnvironment={buildEnvironment}
          lead={
            <DeclinedPanel
              decline={state.decline}
              onPick={onPick ?? (() => undefined)}
              tablesBelow
            />
          }
        />
      )}

      {state.kind === 'error' && (
        <ErrorPanel
          variant={state.variant}
          code={state.code}
          status={state.status}
          onRetry={onRetry}
        />
      )}

      {state.kind === 'answered' && (
        <AnswerCard
          result={state.result as TurnResultPayload}
          buildEnvironment={buildEnvironment}
          incomplete={state.incomplete}
          actions={actions}
        />
      )}
    </div>
  );
}
